import { Component, ElementRef, HostBinding, HostListener, Input, OnInit, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import * as d3 from 'd3';
import { sankey, sankeyLinkHorizontal, SankeyGraph } from 'd3-sankey';
import { FilterService } from 'src/app/services/filter.service';
import { filter, map } from 'rxjs/operators';
import { RouterHandlerService } from 'src/app/services/router-handler.service';
import { ActivatedRoute } from '@angular/router';
import { NavigationService } from 'src/app/services/navigation.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { GridStateService } from 'src/app/services/grid-state.service';

@Component({
  selector: 'app-sankey-chart',
  templateUrl: './sankey-chart.component.html',
  styleUrls: ['./sankey-chart.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SankeyChartComponent implements OnInit {
  @ViewChild("sankeyChartContainer", { static: true }) sankeyChartContainer!: ElementRef;
  @Input('data') data: any;
  @Input('triageTotaldata') triageTotaldata!: any[]|any;
  @Input('pageKey') pageKey: any;
  @Input('item') item: any;
  @Input('config') config: any;
  @Input('headerConfig') headerConfig: any;
  @Input('heading') heading: string = '';
  @ViewChild('fs') fs!: ElementRef;
  @HostBinding('class.is-fullscreen') isFullscreen = false;
  isActive = false;
  iconList: any[] = [];
  isLoading: boolean = false;
  sankeyData: any;
  props: any = { chartHeight: 600 }; // Initialize props object
  divId: string = 'SankeyChartDiv';
  noData: Boolean = false;
  initiateChart: Boolean = false;
  showTotalTriaged: boolean = false;

  @HostListener('fullscreenchange', ['$event'])
  @HostListener('webkitfullscreenchange', ['$event'])
  @HostListener('mozfullscreenchange', ['$event'])
  @HostListener('MSFullscreenChange', ['$event'])
  screenChange(event: any) {
    // This listener detects *any* change in fullscreen state.
    // We check the actual state to determine if we just exited.
    if (!document.fullscreenElement) {
      this.closeFullscreen();
    }
  }

  private tooltip: any;
  private expandedNodes: Set<number> = new Set();
  private layerPositions: number[] = [];
  private layerNames: string[] = [];
  private layerOrder: number[] = [];
  private draggedLayer: number = -1;
  private dragStartX: number = 0;
  private isDraggingHeader: boolean = false;
  private resizeObserver: ResizeObserver | null = null;
  private dynamicHeight: number = 600; // Default initial height
  private numLayers: number = 0;
  private flatData: any[] = [];
  private draggedLayerSelection: any = null;
  private currentDragIndex: number = -1;
  private nodeColorsMap: Map<string, string> = new Map();
  private baseNodeHeight: number = 20;
  private nodePadding: number = 10;
  private layerTotals: Map<number, number> = new Map();
  moduleName: any;
  clientKey: any;
  moduleKey: any;
  dashboardKey: any;
  cardName: any;
  decryptedParams: any;

  // Custom color palette
  private customColorPalette: string[] = [
    '#FF9A3D', "#E74028", "#2CB239", "#25D3EB", "#FFB039", "#1566DE", "#7848FF", "#7AD045", "#CB04DC",
    "#FA195C", "#9E0000", "#EACD37", "#DD630B", "#BCD03F", "#14BDA9", "#8A05F3", "#D9476A",
    "#2DADB6", "#E468C2", "#199CD4", "#0934CC", '#1F2B97', '#2B6BCB', '#2F3CAF', '#3E85EF',
    '#404CBB', '#4832A1', '#49ACA0', '#58B3B9', '#5EDE6B', '#663DBC', '#6651BE', '#6BCFDD',
    '#7458E7', '#7559C5', '#81B661', '#B04747', '#BB3847', '#BBAB54', '#BFC23D', '#C45B0F',
    '#C75FD0', '#D0662A', '#D25297', '#DD6287', '#DE7D48', '#E29063', '#FF5740', '#FFC164'
  ];

  // Available layer name options
  private layerNameOptions: { index: number; key: string; value: string; selected: boolean; filter: string }[] = [];

  constructor(
    private filterService: FilterService,
    private routerHandlerService: RouterHandlerService,
    private route: ActivatedRoute,
    private navigationService: NavigationService,
    private localStorage: LocalStorageService,
    private gridStateService: GridStateService
  ) {
    this.filterService.filterQuery.subscribe((query: any) => {
      this.isLoading = true;
    });
    this.route.params.subscribe((p: any) => {
      this.moduleName = p['module'] || '';
      this.cardName = p['cardName'] || '';
      this.route.queryParams.subscribe((params: any) => {
        this.decryptedParams = this.navigationService.decryptData(params);
        this.clientKey = this.decryptedParams["cl_key"];
        this.moduleKey = this.decryptedParams["md_key"];
        this.dashboardKey = this.decryptedParams["ds_key"];
      });
    });
  }

  fullscreenChanges(fullscreen: any) {
    if (fullscreen == false) {
      // If the function is called with false explicitly, exit fullscreen
      this.closeFullscreen();
      return;
    }
    // Enter fullscreen: Set height based on outerHeight
    this.dynamicHeight = window.outerHeight - 60;
    this.props.chartHeight = this.dynamicHeight;
    this.isFullscreen = true;
    this.isActive = true;
    this.plotChart();
  }

  closeFullscreen(): void {
    // Exit fullscreen: Recalculate dynamic height based on data
    this.isFullscreen = false;
    if (document.fullscreenElement) {
      document.exitFullscreen(); // Ensure native exit if needed
    }
    // Recalculate height based on data when exiting fullscreen
    this.dynamicHeight = this.calculateDynamicHeight();
    this.props = { chartHeight: this.dynamicHeight }; // Update props object
    this.isActive = false;
    setTimeout(() => {
      this.plotChart();
    }, 100);
  }

  ngOnInit(): void {
    this.gridStateService.saveGridStates({
      id: 'widget_20250908080007',
      config: { search: null, filterValue: null }
    });
    
    // let layerValue = this.headerConfig.filter((h: any) => h.value=='icons')[0]
    // this.layerNameOptions = layerValue.add_toggle ? layerValue.layer_name : [] ;
    this.isLoading = true;
    this.initiateCharts();
    let layerValue = this.headerConfig.filter((h: any) => h.value == 'icons')[0];
    this.localStorage.set('sankeyLayerOptions', layerValue.add_toggle ? layerValue.layer_name : []);
    // this.setupResizeObserver();
  }

  layerControl(cfg: any) {
     const triaged = this.localStorage.get<number>('totalTriaged');
     this.showTotalTriaged = cfg.showTotalTriaged
     this.triageTotaldata= cfg.showTotalTriaged ? (triaged||this.triageTotaldata) : [];
     this.layerNameOptions = cfg.selected
     this.localStorage.set('sankeyLayerOptions', this.layerNameOptions);
      for (let i = 1; i <= this.numLayers; i++) {
        const levelKey = `level_${i}`;
        // Check if *every* item in the data has an empty string or just spaces for this levelKey
        const isLevelEmptyForAll = this.data.every((row:any) => {
          const value = row[levelKey];
          // Check for null, undefined, empty string, or string with only whitespace
          return value == null || value == undefined || value == " " || value.trim() == "";
        });
        // If all values for this level are empty/blank, find the corresponding option and set selected to false
        if (isLevelEmptyForAll) {
        const option = this.layerNameOptions.find(o => o.key === levelKey);

        if (option) {
            const index = this.layerNameOptions.indexOf(option);
            if (index !== -1) {
              this.layerNameOptions.splice(index, 1);
            }
          }
        }
      }
    const { newHeaders, newData } = this.transformData(this.layerNameOptions, this.data);
    this.layerNameOptions = newHeaders;
    this.flatData = newData;
    this.getSankeyData(this.flatData);
    this.setupResizeObserver();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Recalculate height dynamically only if not in fullscreen mode
    const triaged = this.localStorage.get<number>('totalTriaged');
    this.triageTotaldata= this.showTotalTriaged ? (triaged||this.triageTotaldata) : [];
    if (changes['data']?.currentValue !== changes['data']?.previousValue && this.initiateChart && !this.isFullscreen) {
      this.layerNameOptions = this.localStorage.get<any[]>('sankeyLayerOptions') || this.layerNameOptions;
      for (let i = 1; i <= this.numLayers; i++) {
        const levelKey = `level_${i}`;
        // Check if *every* item in the data has an empty string or just spaces for this levelKey
        const isLevelEmptyForAll = this.data.every((row:any) => {
          const value = row[levelKey];
          // Check for null, undefined, empty string, or string with only whitespace
          return value == null || value == undefined || value == " " || value.trim() == "";
        });
        // If all values for this level are empty/blank, find the corresponding option and set selected to false
        if (isLevelEmptyForAll) {
        const option = this.layerNameOptions.find(o => o.key === levelKey);

        if (option) {
            const index = this.layerNameOptions.indexOf(option);
            if (index !== -1) {
              this.layerNameOptions.splice(index, 1);
            }
          }
        }
      }
      const { newHeaders, newData } = this.transformData(this.layerNameOptions, this.data);
      this.layerNameOptions = newHeaders;
      this.flatData = newData;
      this.getSankeyData(this.flatData); // This calls plotChart internally after setting height
      this.setupResizeObserver();
    }
  }

  transformData(headers: any[], data: any[]) {
    // Step 1: Keep only selected headers
    const activeHeaders = headers.filter(h => h.selected);
    // Step 2: Reindex headers as level1, level2, ...
    const newHeaders = activeHeaders.map((h, i) => ({
      index: i,
      key: `level_${i + 1}`,
      value: h.value,
      filter: h.filter,
      selected: true
    }));
    // Step 3: Reindex data with the new headers
    const newData = data.map(row => {
      const newRow: any = {};
      activeHeaders.forEach((h, i) => {
        newRow[`level_${i + 1}`] = row[h.key];
      });
      newRow.patient_count = row.patient_count;
      return newRow;
    });
    // Return both
    return { newHeaders, newData };
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private setupResizeObserver(): void {
    const container = document.getElementById(this.divId);
    if (container) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          this.handleResize(entry.contentRect.width, entry.contentRect.height);
        }
      });
      this.resizeObserver.observe(container);
    }
  }

  private handleResize(width: number, height: number): void {
    // Recalculate dynamic height based on data and container *only if not fullscreen*
    if (!this.isFullscreen) {
      this.dynamicHeight = this.calculateDynamicHeight();
      this.props = { chartHeight: this.dynamicHeight };
    }
    this.updateLayerPositions(width);
    this.plotChart(true);
  }

  private calculateDynamicHeight(): number {
    if (!this.sankeyData || !this.sankeyData.nodes || this.sankeyData.nodes.length === 0) {
      return 400; // Default height
    }

    // Group nodes by layer
    const nodesByLayer: { [key: number]: any[] } = {};
    this.sankeyData.nodes.forEach((node: any) => {
      const layerIndex = node.layerIndex;
      if (!nodesByLayer[layerIndex]) {
        nodesByLayer[layerIndex] = [];
      }
      nodesByLayer[layerIndex].push(node);
    });

    // Find the layer with the maximum number of nodes
    let maxNodesInALayer = 0;
    Object.values(nodesByLayer).forEach(layerNodes => {
      if (layerNodes.length > maxNodesInALayer) {
        maxNodesInALayer = layerNodes.length;
      }
    });

    // Calculate height based on the layer with the most nodes
    const heightPerNode = this.baseNodeHeight + this.nodePadding;
    const minHeight = 500;
    const calculatedHeight = Math.max(minHeight, (maxNodesInALayer * heightPerNode) + 150); // Add padding for headers/margins

    return calculatedHeight;
  }

  private updateLayerPositions(width: number): void {
    const margins = this.getMargins(width);
    this.layerPositions = this.calculateLayerPositions(width, margins, this.numLayers);
  }

  private getMargins(width: number): any {
    return {
      top: 60,
      right: Math.min(150, width * 0.15),
      bottom: 40,
      left: Math.min(50, width * 0.1)
    };
  }

  private calculateLayerPositions(width: number, margins: any, numLayers: number): number[] {
    if (numLayers <= 1) return [margins.left];
    const availableWidth = width - margins.left - margins.right;
    const positions: number[] = [];
    for (let i = 0; i < numLayers; i++) {
      const position = margins.left + (i * availableWidth) / (numLayers - 1);
      positions.push(Math.round(position));
    }
    if (positions.length > 0) {
      positions[0] = margins.left;
    }
    if (positions.length > 1) {
      positions[positions.length - 1] = width - margins.right;
    }
    return positions;
  }

  initiateCharts(): void {
    const myClass = this.divId;
    const svg = d3.select('#' + myClass)
      .append('svg')
      .attr('id', 'svg_' + myClass)
      .attr('width', '100%')
      .attr('height', this.dynamicHeight) // Use initial dynamicHeight
      .style('background-color', 'white');

    svg.append('g').attr('class', 'sankeyGroup');

    this.tooltip = d3.select('#SankeyChartDiv') // or the container you fullscreen
      .append('div')
      .attr('class', 'sankey-tooltip')
      .style('position', 'absolute')
      // .style('background', 'white')
      // .style('padding', '8px')
      // .style('border', '1px solid #9b9b9bff')
      // .style('border-radius', '4px')
      // .style('box-shadow', '0 2px 4px rgba(0,0,0,0.2)')
      .style('pointer-events', 'none')
      // .style('opacity', 0)
      .style('z-index', '1000');

    this.initiateChart = true;
  }

  // Generate colors based on node index for consistent assignment
  private generateNodeColors(nodes: any[]): void {
    this.nodeColorsMap.clear();
    // Get all unique node names and sort them for consistent indexing
    const uniqueNodeNames = Array.from(new Set(nodes.map(node => node.name))).sort();
    // Assign colors based on index in the sorted array
    uniqueNodeNames.forEach((nodeName, index) => {
      const colorIndex = index % this.customColorPalette.length;
      const color = this.customColorPalette[colorIndex];
      this.nodeColorsMap.set(nodeName, color);
    });
  }

  // Get color for a node
  private getNodeColor(nodeName: string): string {
    return this.nodeColorsMap.get(nodeName) || '#4682b4';
  }

  // Calculate total value for a specific layer
  private calculateLayerTotals(): void {
    this.layerTotals.clear();
    // Initialize totals for each layer
    for (let i = 0; i < this.numLayers; i++) {
      this.layerTotals.set(i, 0);
    }
    // Sum values for each layer
    this.sankeyData.nodes.forEach((node: any) => {
      const currentTotal = this.layerTotals.get(node.layerIndex) || 0;
      this.layerTotals.set(node.layerIndex, currentTotal + (node.value || 0));
      // this.layerTotals.set(node.layerIndex, this.triageTotaldata.value);
    });
  }

  // Get percentage for a node based on its layer total
  private getNodePercentage(node: any): string {
    let layerTotal = this.layerTotals.get(node.layerIndex) || 0;
    if (layerTotal === 0) return '0.0';
    layerTotal = this.triageTotaldata.length>0 ? this.triageTotaldata[0].value : layerTotal;
    return ((node.value / layerTotal) * 100).toFixed(2);
  }

  plotChart(animate: boolean = true): void {
    const myChart: any = this;
    const svg: any = d3.select('#svg_' + this.divId);
    const container = svg.node()?.parentElement;
    if (!container) return;

    let width: number = container.getBoundingClientRect().width;
    // Use the height stored in this.props.chartHeight, which is managed by the logic
    let height: number = this.props.chartHeight;

    svg.attr('width', width).attr('height', height);

    const margins = this.getMargins(width);
    if (this.layerPositions.length !== this.numLayers) {
      this.updateLayerPositions(width);
    }

    myChart.layerNameOptions.map((layer:any) => { layer.value = (myChart.filterService?.baseQuery?.['source_type']=='Non Commercial' && layer.filter=="source_nm") ? layer.value="Program Type" : layer.value; });
    const NODE_WIDTH = 10;
    const MIN_NODE_HEIGHT = 20;

    const layerPositionMap: Map<number, number> = new Map();
    this.layerOrder.forEach((layerIndex, position) => {
      layerPositionMap.set(layerIndex, position);
    });

    // Rebuild sankey data based on current layer order
    const { nodes, links } = this.buildSankeyData(this.flatData);
    // console.log('Built sankey data:', { nodes, links });
    this.sankeyData = { nodes, links };

    // Calculate layer totals for percentage calculation
    this.calculateLayerTotals();

    // Generate colors based on node index
    this.generateNodeColors(nodes);

    const workingNodes = this.sankeyData.nodes.map((node: any) => ({ ...node }));
    const workingLinks = this.sankeyData.links.map((link: any) => ({ ...link }));

    const filteredLinks = workingLinks.filter((link: any) => {
      const sourceNode = workingNodes.find((n: any) => n.index === link.source);
      const targetNode = workingNodes.find((n: any) => n.index === link.target);
      if (!sourceNode || !targetNode) return false;
      const sourcePosition = layerPositionMap.get(sourceNode.layerIndex) || 0;
      const targetPosition = layerPositionMap.get(targetNode.layerIndex) || 0;
      return sourcePosition < targetPosition;
    });

    try {
      const graph: SankeyGraph<any, any> = sankey()
    .nodeWidth(NODE_WIDTH)
    .nodePadding(width < 768 ? 15 : 25)
    .nodeAlign((node: any) => layerPositionMap.get(node.layerIndex) ?? node.layerIndex)
    .nodeSort((a: any, b: any) => d3.ascending(a.order, b.order))
    .linkSort((a: any, b: any) => d3.ascending(a.source.order, b.source.order))
    .extent([
        [margins.left, margins.top],
        [width - margins.right, height - margins.bottom]
    ])({
        nodes: workingNodes,
        links: filteredLinks
    });

if (!this.isFullscreen) {
    const minY = d3.min(graph.nodes, (d: any) => d.y0) || 0;
    const maxY = d3.max(graph.nodes, (d: any) => d.y1) || 300;

    const requiredHeight = (maxY - minY) + 120;  // header + padding buffer
    this.dynamicHeight = Math.max(450, requiredHeight);
    this.props.chartHeight = this.dynamicHeight;

    svg.attr("height", this.dynamicHeight);
}
      // Adjust node positions for dynamic height
      graph.nodes.forEach((node: any) => {
        const originalLayer = node.layerIndex;
        const orderedIndex = this.layerOrder.indexOf(originalLayer);
        if (orderedIndex >= 0 && orderedIndex < this.layerPositions.length) {
          node.x0 = this.layerPositions[orderedIndex];
          node.x1 = node.x0 + NODE_WIDTH;
        }
        // Ensure minimum node height
        if (node.y1 - node.y0 < MIN_NODE_HEIGHT) {
          const centerY = (node.y0 + node.y1) / 2;
          node.y0 = centerY - MIN_NODE_HEIGHT / 2;
          node.y1 = centerY + MIN_NODE_HEIGHT / 2;
        }
      });

      svg.select('.sankeyGroup').select('defs').remove();
      const defs = svg.select('.sankeyGroup').append('defs');

      const validLinks = graph.links.filter((link: any) => {
        return link.source && link.target &&
          link.source.x1 !== undefined && link.target.x0 !== undefined &&
          link.source.y0 !== undefined && link.source.y1 !== undefined &&
          link.target.y0 !== undefined && link.target.y1 !== undefined &&
          !isNaN(link.source.x1) && !isNaN(link.target.x0) &&
          !isNaN(link.source.y0) && !isNaN(link.source.y1) &&
          !isNaN(link.target.y0) && !isNaN(link.target.y1);
      });

      validLinks.forEach((link: any) => {
        const gradient = defs.append('linearGradient')
          .attr('id', `gradient-${link.index}`)
          .attr('gradientUnits', 'userSpaceOnUse')
          .attr('x1', link.source.x1)
          .attr('y1', (link.source.y0 + link.source.y1) / 2)
          .attr('x2', link.target.x0)
          .attr('y2', (link.target.y0 + link.target.y1) / 2);

        gradient.append('stop')
          .attr('offset', '0%')
          .attr('stop-color', this.getNodeColor(link.source.name));
        gradient.append('stop')
          .attr('offset', '100%')
          .attr('stop-color', this.getNodeColor(link.target.name));
      });

      const link = svg.select('.sankeyGroup')
        .selectAll('.link')
        .data(validLinks, (d: any) => d.index);

      const linkEnter = link.enter()
        .append('path')
        .attr('class', 'link')
        .attr('d', sankeyLinkHorizontal())
        .attr('stroke', (d: any) => `url(#gradient-${d.index})`)
        .attr('stroke-width', (d: any) => Math.max(1, d.width || 1))
        .attr('fill', 'none')
        .style('cursor', 'default')
        .attr('stroke-opacity', 0.6)
        .on('mouseover', (event: any, d: any) => {
          this.showTooltip(event, d, 'link', width, height);
          d3.select(event.currentTarget).attr('stroke-opacity', 0.9);
        })
        .on('mousemove', (event: any, d: any) => {
          this.showTooltip(event, d, 'link', width, height);
          d3.select(event.currentTarget).attr('stroke-opacity', 0.9);
        })
        .on('mouseout', (event: any, d: any) => {
          this.hideTooltip();
          d3.select(event.currentTarget).attr('stroke-opacity', 0.6);
        });

      const linkMerge = linkEnter.merge(link);

      if (animate) {
        linkMerge
          .transition()
          .duration(300)
          .attr('d', sankeyLinkHorizontal())
          .attr('stroke-width', (d: any) => Math.max(1, d.width || 1));
      } else {
        linkMerge
          .attr('d', sankeyLinkHorizontal())
          .attr('stroke-width', (d: any) => Math.max(1, d.width || 1));
      }

      link.exit().remove();

      const validNodes = graph.nodes.filter((node: any) => {
        return node.x0 !== undefined && node.x1 !== undefined &&
          node.y0 !== undefined && node.y1 !== undefined &&
          !isNaN(node.x0) && !isNaN(node.x1) &&
          !isNaN(node.y0) && !isNaN(node.y1) &&
          node.x1 > node.x0 && node.y1 > node.y0;
      });

      const node = svg.select('.sankeyGroup')
        .selectAll('.node')
        .data(validNodes, (d: any) => d.index);

      const nodeEnter = node.enter()
        .append('g')
        .attr('class', 'node')
        .on('mouseover', (event: any, d: any) => {
          this.showTooltip(event, d, 'node', width, height);
          d3.select(event.currentTarget).select('rect').attr('stroke-width', 2);
        })
        .on('mousemove', (event: any, d: any) => {
          this.showTooltip(event, d, 'node', width, height);
          d3.select(event.currentTarget).select('rect').attr('stroke-width', 2);
        })
        .on('mouseout', (event: any, d: any) => {
          this.hideTooltip();
          d3.select(event.currentTarget).select('rect').attr('stroke-width', 1);
        });

      const nodeMerge = nodeEnter.merge(node);

      if (animate) {
        nodeMerge
          .transition()
          .duration(300)
          .attr('transform', (d: any) => `translate(${d.x0},${d.y0})`);
      } else {
        nodeMerge
          .attr('transform', (d: any) => `translate(${d.x0},${d.y0})`);
      }

      nodeMerge.selectAll('rect').remove();
       svg.selectAll('.node').raise();
      // Store reference to component for use in D3 callbacks
      const self = this;
      const rects = nodeMerge.append('rect')
        .attr('width', (d: any) => Math.max(NODE_WIDTH, d.x1 - d.x0))
        .attr('height', (d: any) => Math.max(MIN_NODE_HEIGHT, d.y1 - d.y0))
        .attr('fill', (d: any) => this.getNodeColor(d.name))
        .attr('stroke', '#333')
        .attr('stroke-width', 1)
        .attr('rx', 4)
        .attr('ry', 4);

      if (animate) {
        rects
          .transition()
          .duration(300)
          .attr('width', (d: any) => Math.max(NODE_WIDTH, d.x1 - d.x0))
          .attr('height', (d: any) => Math.max(MIN_NODE_HEIGHT, d.y1 - d.y0));
      }

      nodeMerge.selectAll('text').remove();
      nodeMerge.selectAll('svg').remove(); // Remove old icons

      nodeMerge.each(function (d: any) {
        // console.log('Rendering node:', d);
       if( self.triageTotaldata.length > 0 && (d.name == self.triageTotaldata[0].name)){
        d.value=self.triageTotaldata[0].value;
       }
        // @ts-ignore
        const group = d3.select(this);
        //calculations
        const nodeWidth = Math.max(NODE_WIDTH, d.x1 - d.x0);
        const nodeHeight = Math.max(MIN_NODE_HEIGHT, d.y1 - d.y0);
        const percentage = d.value > 0 ? self.getNodePercentage(d) : '0.0';
        const valueText = `${d.value?.toLocaleString() || 0} (${percentage}%)`;
        const baseFontSize = width < 480 ? 8 : width < 768 ? 10 : 14;
        const fontSize = Math.min(baseFontSize, Math.max(myChart.isfullscreen?14:10, nodeHeight / 8));
        const lines = [d.name, valueText];
        const lineHeight = Math.min(fontSize + 4, nodeHeight / 2.5);
        const startY = (nodeHeight / 2) - ((lines.length - 1) * lineHeight) / 2;

        lines.forEach((line, i) => {
          const textX = 12//width < 768 ? 0 : 12;
          const textY = Math.max(lineHeight, startY + i * lineHeight) +( i==1 ? 3 : 0);

          const textElem = group.append('text').raise()
            .attr('x', textX)
            .attr('y', i === 0 ? textY : textY + 2)
            .attr('text-anchor', 'start')
            .style('font-size', i === 0 ? `${fontSize + 1}px` : `${fontSize}px`)
            .style('font-weight', i === 0 ? 'bold' : 'normal')
            .style('font-family', 'poppins')
            .style('fill', '#333')  
            .style('cursor', (i === 0 && !self.showTotalTriaged) ? 'pointer' : ((i === 0 && myChart.showTotalTriaged && d.name!='Triaged Patients') ? 'pointer' : 'default') )
            //text and the value
            .text(line.length > (width < 480 ? 20 : width < 768 ? 25 : 30) ?
              line.substring(0, width < 480 ? 20 : width < 768 ? 25 : 30) + '...' : line);

          // Only add icon beside the first line (d.name)
          if (i === 0) {
            // const textBBox = textElem.node()!.getBBox();
            // const iconSize = fontSize; // proportional to text
            // const iconX = textX + textBBox.width + 4;
            // const iconY = textY - iconSize + 2;

            // const icon = group.append('svg')
            //   .attr('x', iconX)
            //   .attr('y', iconY)
            //   .attr('width', iconSize)
            //   .attr('height', iconSize)
            //   .attr('viewBox', '0 0 8 8')
            //   .attr('fill', 'none')
            //   .style('cursor', 'pointer')
            //   .html(`
            //     <path class="icon-path" d="M0.0132942 0.107478C-0.0138363 0.172516 0.00147353 0.247304 0.0508594 0.297049L3.75416 4.00001L0.0512184 7.70295C0.00147385 7.7527 -0.0134772 7.82817 0.0133108 7.89252C0.0400825 7.95756 0.103735 8 0.17399 8L3.30442 8C3.35068 8 3.39486 7.98156 3.42755 7.94921L7.25364 4.12312C7.28738 4.08903 7.30443 4.04452 7.30443 3.99999C7.30443 3.95546 7.28739 3.91095 7.25364 3.87686L3.42755 0.0507716C3.39486 0.0184239 3.35068 -1.63589e-05 3.30442 -1.63569e-05L0.17399 -1.62201e-05C0.103734 -4.53439e-09 0.0400822 0.0424399 0.0132942 0.107478Z" fill="#1363DF"/>
            //   `);

            // Hover effect for both text + icon
            function setHover(active: boolean) {
              textElem.raise()
              textElem.style('fill',  (active && !myChart.showTotalTriaged ) ? '#1469E1' : ((active && myChart.showTotalTriaged && d.name!='Triaged Patients') ? '#1469E1' : '#333'));
              // icon.select('path').attr('fill', active ? 'blue' : '#1363DF');
            }

            // Apply hover/click for icon
            // icon.on('mouseover', () => setHover(true))
            //     .on('mouseout', () => setHover(false))
            //     .on('click', () => {
            //       console.log('Icon clicked for node:', d);
            //     });

            // Apply hover/click for text
            textElem.on('mouseover', () => setHover(true))
              .on('mouseout', () => setHover(false))
              .on('click', async () => {

                const payload = myChart.layerNameOptions.find((v: any) => v.index === d.layerIndex);
                // if (!payload) return;

                // Prevent navigating when Total Triaged is enabled
                if (myChart.showTotalTriaged && d.name === 'Triaged Patients') return;

                // Build base payload
                const myData: any = { buffer_days: myChart.filterService.buffer_days };

                if (payload.filter === 'hashtag_value') {
                  const search = { hashtag_value: d.name, search_prm: 'TAG' };

                   myChart.gridStateService.saveGridStates({
                    id: 'widget_20250908080007',
                    config: { search,filterValue:d.name }
                  });

                  // No extra filter added
                } else {
                  myData[payload.filter] = d.name;
                }

                myChart.navigationToDetailPage(myData);
              });

          }
        });
      });

      node.exit().remove();

      this.addLayerHeaders(svg, width, height, margins);
    } catch (error) {
      console.error('Error in plotChart:', error);
    }
    this.isLoading = false;
  }

  private addLayerHeaders(svg: any, width: number, height: number, margins: any): void {
    svg.selectAll('.layer-headers').remove();
    const headerGroup = svg.append('g')
      .attr('class', 'layer-headers');

    this.layerOrder.forEach((layerIndex, orderIndex) => {
      const x = orderIndex < this.layerPositions.length ? this.layerPositions[orderIndex] :
        margins.left + (orderIndex * (width - margins.left - margins.right) / (this.numLayers - 1));

      const header = headerGroup.append('g')
        .attr('class', 'layer-header')
        .style('cursor', 'grab')
        .attr('transform', `translate(${x}, ${margins.top - 25})`)
        .datum({ layerIndex, orderIndex }); // Store data for easier access

      const dragIcon = header.append('g')
        .attr('class', 'drag-symbol')
        .attr('transform', 'translate(-20, -5)')
        .style('cursor', 'grab');

      // background rectangle
      dragIcon.append('rect')
        .attr('x', -7)       // left padding
        .attr('y', -6)       // top padding
        .attr('width', 20)   // adjust width
        .attr('height', 24)  // adjust height
        .attr('rx', 4)       // rounded corners
        .attr('ry', 4)
        .attr('fill', '#fff');  // background color

      // create 6-dot grip inside
      const circles = [
        [0, 0], [5, 0],
        [0, 5], [5, 5],
        [0, 10], [5, 10]
      ];
      dragIcon.selectAll('circle')
        .data(circles)
        .enter()
        .append('circle')
        .attr('cx', (d: any) => d[0])
        .attr('cy', (d: any) => d[1])
        .attr('r', 1.5)
        .attr('fill', 'gray');

      // Add layer name text directly from layerOrder
      const actualLayerIndex = this.layerOrder[orderIndex];
      const layerOption = this.layerNameOptions.find(option => option.index === actualLayerIndex);
      const layerName = layerOption ? layerOption.value : `Layer ${actualLayerIndex + 1}`;

      const textElement = header.append('text')
        .attr('class', 'layer-header-text')
        .attr('x', 0)
        .attr('y', 6)
        .attr('text-anchor', 'start')
        .style('font-size', '14px')
        .style('font-weight', 'bold')
        .style('font-family', 'poppins')
        .style('fill', '#101D42')
        .text(layerName).on('mouseover', () => {
          // @ts-ignore
          d3.select(this)
            .style('fill', this.showTotalTriaged ?'#1463DE':'#101D42')  // hover color
            .style('font-weight', 'bold');
        })
        .on('mouseout', function () {
          // @ts-ignore
          if (!d3.select(this).classed('active')) {
            // @ts-ignore
            d3.select(this)
              .style('fill', '#101D42') // normal color
              .style('font-weight', 'bold');
          }
        })
        .on('click',  ()=> {
          // @ts-ignore
          const self = d3.select(this);
          const isActive = self.classed('active');
          if(this.showTotalTriaged)return

          // reset all texts first (if you want only one active at a time)
          d3.selectAll('.layer-header-text')
            .classed('active', false)
            .style('fill', '#1463DE')
            .style('font-weight', 'bold');

          if (!isActive) {
            self.classed('active', true)
              .style('fill', 'red')   // active color
              .style('font-weight', 'bold');
          }
        });

      const dragHeader = d3.drag()
        .touchable(true)
        .on('start', (event: any, d: any) => {
          // Prevent default touch behavior to avoid scrolling
          if (event.sourceEvent && event.sourceEvent.type === "touchstart") {
            event.sourceEvent.preventDefault();
          }
          this.draggedLayer = layerIndex;
          this.draggedLayerSelection = d3.select(event.sourceEvent?.target?.closest('.layer-header') || event?.target?.closest('.layer-header'));
          this.currentDragIndex = this.layerOrder.indexOf(layerIndex);
          this.dragStartX = event.x || (event.touches && event.touches[0] ? event.touches[0].clientX : 0);
          this.isDraggingHeader = true;
          if (this.draggedLayerSelection) {
            this.draggedLayerSelection.raise().classed('dragging', true);
          }
        })
        .on('drag', (event: any, d: any) => {
          if (!this.isDraggingHeader || this.draggedLayer !== layerIndex) return;
          const currentX = event.x || (event.touches && event.touches[0] ? event.touches[0].clientX : 0);

          // Calculate new index based on position
          const margins = this.getMargins(width);
          const availableWidth = width - margins.left - margins.right;
          const slotWidth = availableWidth / (this.numLayers - 1);
          const relativeX = currentX - margins.left;
          let newIndex = Math.round(relativeX / slotWidth);

          // Clamp to valid range
          newIndex = Math.max(0, Math.min(this.layerOrder.length - 1, newIndex));

          // Only update if index changed
          if (newIndex !== this.currentDragIndex) {
            // Swap in layerOrder array
            const temp = this.layerOrder[this.currentDragIndex];
            this.layerOrder[this.currentDragIndex] = this.layerOrder[newIndex];
            this.layerOrder[newIndex] = temp;

            // Swap the corresponding data in flatData
            const keyMap = this.generateLevelKeys(this.numLayers);
            this.flatData = this.swapKeysInArray(this.flatData, keyMap[this.currentDragIndex], keyMap[newIndex]);

            // Update current index
            this.currentDragIndex = newIndex;

            // Update layerNameOptions to sync with new order
            this.updateLayerNameOptions();

            // Update all layer header texts immediately
            this.updateLayerHeaderNames(svg, width, height, margins);

            // Update visualization immediately with updated layer names
            this.updateLayerPositions(width);
          }

          // Move the dragged element with the cursor
          if (this.draggedLayerSelection) {
            const x = currentX - 60; // Adjust for element width
            this.draggedLayerSelection.attr('transform', `translate(${x}, ${margins.top - 25})`);
          }
        })
        .on('end', (event: any, d: any) => {
          this.isDraggingHeader = false;
          if (this.draggedLayerSelection) {
            // Animate back to final position
            const finalIndex = this.layerOrder.indexOf(layerIndex);
            const finalX = finalIndex < this.layerPositions.length ? this.layerPositions[finalIndex] :
              margins.left + (finalIndex * (width - margins.left - margins.right) / (this.numLayers - 1));
            this.draggedLayerSelection
              .classed('dragging', false)
              .transition()
              .duration(250)
              .attr('transform', `translate(${finalX}, ${margins.top - 25})`)
              .on('end', () => {
                this.draggedLayer = -1;
                this.draggedLayerSelection = null;
                this.currentDragIndex = -1;
                this.plotChart(); // Final update
              });
          } else {
            this.draggedLayer = -1;
            this.draggedLayerSelection = null;
            this.currentDragIndex = -1;
            this.plotChart(); // Final update
          }
        });

      header.call(dragHeader);
    });
  }

  // Update layerNameOptions to sync with current layerOrder
  private updateLayerNameOptions(): void {
    // Create a copy of the current layerNameOptions
    const updatedOptions = [...this.layerNameOptions];
    // Update the indices to match the current layerOrder
    this.layerOrder.forEach((layerIndex, orderIndex) => {
      const option = updatedOptions.find(opt => opt.index === layerIndex);
      if (option) {
        // Update the index to match the visual order
        option.index = orderIndex;
      }
    });
    // Sort by the new indices to maintain proper order
    this.layerNameOptions = updatedOptions.sort((a, b) => a.index - b.index);
    // setTimeout(() => {
    // this.plotChart();
    this.getSankeyData(this.flatData);
    // }, 200);
  }

  // Update all layer header names based on current layerOrder
  private updateLayerHeaderNames(svg: any, width: number, height: number, margins: any): void {
    const headerGroup = svg.select('.layer-headers');
    if (headerGroup.empty()) return;

    headerGroup.selectAll('.layer-header').each((d: any, i: number, nodes: any) => {
      const header = d3.select(nodes[i]);
      const orderIndex = i;
      const actualLayerIndex = this.layerOrder[orderIndex];
      const layerOption = this.layerNameOptions.find(option => option.index === orderIndex);
      const layerName = layerOption ? layerOption.value : `Layer ${orderIndex + 1}`;

      // Update the text element
      header.select('.layer-header-text').text(layerName);
    });
  }

  private generateLevelKeys(numLayers: number): string[] {
    return Array.from({ length: numLayers }, (_, i) => `level_${i + 1}`);
  }

  private swapKeysInArray<T extends Record<string, any>>(
    data: T[],
    keyA: keyof T,
    keyB: keyof T
  ): T[] {
    return data.map(item => {
      const newItem = { ...item };
      [newItem[keyA], newItem[keyB]] = [newItem[keyB], newItem[keyA]];
      return newItem;
    });
  }

  private getTotalValue(node: any): number {
    const outgoingLinks = this.sankeyData.links.filter((link: any) =>
      link.source === node.index
    );
    return outgoingLinks.reduce((sum: number, link: any) => sum + (link.value || 0), 0);
  }

  private showTooltip(event: any, data: any, type: string, width: any, height: any): void {
    let tooltipContent = '';
    if (type === 'link') {
      const sourceName = data.source?.name || 'Unknown';
      const targetName = data.target?.name || 'Unknown';
      const flowValue = data.value || 0;
      const totalSource = data.source?.value || 0;
      const pctOfSource = totalSource > 0 ? ((flowValue / totalSource) * 100).toFixed(2) : '0.0';
      const linkColor = this.getNodeColor(sourceName);

      tooltipContent = `
      <div class="tooltip-header">Link Details</div>
      <div class="tooltip-body">
        <div class="tooltip-item">
          <div class="bullet" style="background-color: ${linkColor};"></div>
          <span>${sourceName} → ${targetName}: ${flowValue.toLocaleString()} </span>
        </div>
        <div class="tooltip-item">
          <div class="bullet" style="background-color:${linkColor};"></div>
          <span>${pctOfSource}% of source</span>
        </div>
      </div>
    `;
    } else {
      const nodeName = data.name || 'Unknown';
      const nodeValue = data.value || 0;
      const layerTotal =this.triageTotaldata.length>0 ? this.triageTotaldata[0].value : (this.layerTotals.get(data.layerIndex) || 0);
      const pctOfLayer = layerTotal > 0 ? ((nodeValue / layerTotal) * 100).toFixed(2) : '0.0';
      const nodeColor = this.getNodeColor(nodeName);

      tooltipContent = `
      <div class="tooltip-header">Node Details</div>
      <div class="tooltip-body">
        <div class="tooltip-item">
          <div class="bullet" style="background-color: ${nodeColor};"></div>
          <span> ${nodeName}: ${nodeValue.toLocaleString()}</span>
        </div>
        <div class="tooltip-item">
          <div class="bullet" style="background-color:${nodeColor};"></div>
          <span>  Percentage: ${pctOfLayer}% </span>
        </div>
        <div class="tooltip-item">
          <div class="bullet" style="background-color: ${nodeColor};"></div>
          <span>Layer Total: ${layerTotal.toLocaleString()}</span>
        </div>
      </div>
    `;
    }

    // Prepare tooltip for measurement
    this.tooltip
      .html(tooltipContent)
      .style('opacity', 0)
      .style('display', 'block')

    const tooltipEl = this.tooltip.node() as HTMLElement;
    const tooltipWidth = tooltipEl.offsetWidth || 220;
    const tooltipHeight = tooltipEl.offsetHeight || 100;

    // Get mouse position (robust for D3/touch/mouse)
    const mouseX = event.offsetX ?? event.sourceEvent?.offsetX ?? (event.touches?.[0]?.clientX ?? 0);
    const mouseY = event.offsetY ?? event.sourceEvent?.offsetY ?? (event.touches?.[0]?.clientY ?? 0);

    const margin = 12;
    const winWidth = width || window.innerWidth;
    const winHeight = height || window.innerHeight;

    // --- Horizontal positioning ---
    let left = mouseX + 10;
    if (mouseX + tooltipWidth + margin > winWidth) {
      // Not enough space on right → show on left
      left = mouseX - tooltipWidth - margin;
    }
    left = Math.max(margin, left); // Prevent overflow on left

    // --- Vertical positioning ---
    let top = mouseY + 10; // Default: BELOW cursor
    let flippedY = false;
    if (mouseY + tooltipHeight + margin > winHeight) {
      // Not enough space below → show ABOVE cursor
      top = mouseY - tooltipHeight - margin;
      flippedY = true;
    }
    top = Math.max(margin, top); // Prevent overflow on top

    // Apply flip classes for arrow direction

    // Final position + show
    this.tooltip
      .style('left', `${left}px`)
      .style('top', `${top}px`)
      .style('display', 'block')
      .style('opacity', 0.95);
  }

  private hideTooltip(): void {
    this.tooltip
      .transition()
      .duration(100)
      .style('opacity', 0);
  }

  getSankeyData(data: any): void {
    this.flatData = data;
    // Dynamically determine number of layers
    this.numLayers = this.determineNumLayers(this.flatData);
    // Initialize layer names from layerNameOptions
    this.layerNames = this.layerNameOptions.map(option => option.value);
    this.layerOrder = Array.from({ length: this.numLayers }, (_, i) => i);

    // Calculate dynamic height *only if not fullscreen*
    if (!this.isFullscreen) {
        this.dynamicHeight = this.calculateDynamicHeight();
        this.props = { chartHeight: this.dynamicHeight };
    }

    if (this.flatData && this.flatData.length > 0) {
      setTimeout(() => {
        const container = document.getElementById(this.divId);
        if (container) {
          const width = container.getBoundingClientRect().width;
          this.updateLayerPositions(width);
          this.plotChart(true);
          this.noData = false;
        }
      }, 0);
    } else {
      this.noData = true;
      this.isLoading = false;
    }
  }

  private determineNumLayers(flatData: any[]): number {
    if (!flatData || flatData.length === 0) return 0;
    const firstItem = flatData[0];
    let numLayers = 0;
    // Count level keys (level1, level2, level3, etc.)
    Object.keys(firstItem).forEach(key => {
      if (key.startsWith('level_') && !isNaN(parseInt(key.replace('level_', '')))) {
        numLayers++;
      }
    });
    return numLayers;
  }

  buildSankeyData(flatData: any[]) {
    const nodesMap = new Map<string, number>(); // key: name_layerIndex -> index
    const nodes: any[] = [];
    const linksMap = new Map<string, any>(); // key: source_target -> link

    function getNodeIndex(name: string, layerIndex: number): number {
      const key = `${name}_${layerIndex}`;
      if (!nodesMap.has(key)) {
        const index = nodes.length;
        nodes.push({ index, name, value: 0, layerIndex });
        nodesMap.set(key, index);
      }
      return nodesMap.get(key)!;
    }

    flatData.forEach(row => {
      // Dynamically extract level data
      const levels: { name: string; idx: number }[] = [];
      for (let i = 1; i <= this.numLayers; i++) {
        const key = `level_${i}`;
        if (row.hasOwnProperty(key) && row[key] !== null && row[key] !== undefined) {
          levels.push({ name: row[key], idx: i - 1 });
        }
      }

      // Remove empty levels
      const nonEmptyLevels = levels.filter(l => l.name && l.name.trim() !== "");

      // Collect node indexes
      const nodeIndexes: number[] = [];
      nonEmptyLevels.forEach(l => {
        const idx = getNodeIndex(l.name, l.idx);
        nodeIndexes.push(idx);
        nodes[idx].value += row.patient_count || 0;
      });

      // Build links and sum duplicates
      for (let i = 0; i < nodeIndexes.length - 1; i++) {
        const source = nodeIndexes[i];
        const target = nodeIndexes[i + 1];
        const key = `${source}_${target}`;
        if (linksMap.has(key)) {
          linksMap.get(key).value += row.patient_count || 0;
        } else {
          linksMap.set(key, { source, target, value: row.patient_count || 0 });
        }
      }
    });

    return { nodes, links: Array.from(linksMap.values()) };
  }

  navigationToDetailPage(data: any) {
    // Set Navigation
    
    const conf0 = this.item.config?.['nav_configuration'].controls[0];
    let nav = {
      dbJSON: conf0?.dbJSON,
      s3JSON: conf0?.s3JSON,
      pageName: conf0?.pageName,
      isDashboardLevel: conf0?.isDashboardLevel,
    };
    this.routerHandlerService.navDetailInfo(nav);
    this.routerHandlerService.storingPayloadAndFilterForGrid(conf0, data, this.cardName);
    let query: any = Object.assign({}, this.decryptedParams, { tab_id: 0 }, { page: 'card' + 'Sankey' });
    let queryMaped = this.navigationService.movePropertyToFirst(query, 'tab_id');
    this.navigationService.routerNavigate(`dashboard/${this.moduleName}/${nav.pageName}`, queryMaped);
  }
}